/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { CREATE_PRESENTATION } from "../graphql";
import { VApplication } from "../../../application";
import notification from "../config/notifications.config.json";
import axios from "axios";
import { LoggerModule } from "@vlinder-web/logger-module-react";
import gql from "graphql-tag";

const log: any = LoggerModule?.getInstance();
const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

export interface CreatePresentationProps {
  name?: string;
  year?: string;
  status?: string;
}

export interface ICreatePresentationMethods extends IPluginMethodMap {
  createPresentation(args: CreatePresentationProps): Promise<any>;
  getTemplateCostumeTypes(): Promise<any>;
  importSectionOptType(args: { presentation: string; templateCostumeType: string }): Promise<any>;
}

const GET_TEMPLATE_COSTUME_TYPES = gql`
  query Content {
    getTemplateCostumeTypes {
      content {
        id
        name
        code
        description
      }
    }
  }
`;

const IMPORT_SECTION_OPT_TYPE = gql`
  mutation ImportSectionOptType($filter: importCostumeType!) {
    importSectionOptType(filter: $filter) {
      id
      name
    }
  }
`;

export class CreatePresentationService implements IAgentPlugin {
  readonly methods: ICreatePresentationMethods;

  constructor() {
    this.methods = {
      createPresentation: this.createPresentation.bind(this),
      getTemplateCostumeTypes: this.getTemplateCostumeTypes.bind(this),
      importSectionOptType: this.importSectionOptType.bind(this),
    };
  }

  async createPresentation(args: CreatePresentationProps) {
    log.blue(`inviteUser server component:`, args);
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");
    try {
      if (client) {
        log.blue(`inviteUser server component222:`, args);

        // Omit any field whose value is an empty string or null
        // to avoid sending invalid values for GraphQL enum fields (e.g. type, status)
        const sanitizeData = (input: any): any =>
          Object.fromEntries(
            Object.entries(input).filter(
              ([, v]) => v !== "" && v !== null && v !== undefined
            )
          );

        const res = await client.mutate({
          mutation: CREATE_PRESENTATION,
          variables: {
            data: sanitizeData(args),
          },
          fetchPolicy: "no-cache",
        });
        if (res?.data) {
          return {
            ...res?.data?.createPresentation,
          };
        }
      }
    } catch (err: any) {
      let _display_message =
      err?.graphQLErrors &&
      Array.isArray(err?.graphQLErrors) &&
      err?.graphQLErrors.find((item: any) => item.extensions);
    if (_display_message?.extensions?.display_message) {
      throw Error(_display_message?.extensions?.display_message);
    }

    throw err;
    }
  }

  async getTemplateCostumeTypes() {
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");
    try {
      if (client) {
        const res = await client.query({
          query: GET_TEMPLATE_COSTUME_TYPES,
          fetchPolicy: "network-only",
        });
        return res?.data?.getTemplateCostumeTypes?.content || [];
      }
    } catch (err: any) {
      const _display_message =
        err?.graphQLErrors &&
        Array.isArray(err?.graphQLErrors) &&
        err?.graphQLErrors.find((item: any) => item.extensions);
      if (_display_message?.extensions?.display_message) {
        throw Error(_display_message?.extensions?.display_message);
      }
      throw err;
    }
  }

  async importSectionOptType(args: { presentation: string; templateCostumeType: string }) {
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");
    try {
      if (client) {
        const res = await client.mutate({
          mutation: IMPORT_SECTION_OPT_TYPE,
          variables: {
            filter: {
              presentation: args.presentation,
              templateCostumeType: args.templateCostumeType,
            },
          },
          fetchPolicy: "no-cache",
        });
        return res?.data?.importSectionOptType ?? { success: true };
      }
    } catch (err: any) {
      const _display_message =
        err?.graphQLErrors &&
        Array.isArray(err?.graphQLErrors) &&
        err?.graphQLErrors.find((item: any) => item.extensions);
      if (_display_message?.extensions?.display_message) {
        throw Error(_display_message?.extensions?.display_message);
      }
      throw err;
    }
  }
}
